import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
// fixed by adding lazy import in useWalletOnboardingModal file
// ESLint can't detect lazy imports in static analysis
// eslint-disable-next-line import/no-cycle
import useFeatures from 'hooks/useFeatures';
import {
  useGetActivationStatus,
  useUpdateActivationStatus,
} from 'hooks/Bank/useActivationStatus';
import useHasAccountableBanking from 'hooks/Bank/useHasAccountableBanking';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCreateBankConnection from 'hooks/Bank/useCreateBankConnection';
import useInitiateAccountableBankingOnboarding from 'hooks/Bank/useInitiateAccountableBankingOnboarding';
import useGetCardsList from 'hooks/Bank/useGetCardsList';
import CustomLoader from 'components/molecules/CustomLoader.component';
import { fadeTransition } from 'utils/constants';
import { logAmplitude } from 'utils/amplitude';
import { AvailableCountries, type IUser } from 'types/users.types';
import type { MonthlyPaymentVolume } from 'types/settings.types';
import {
  getAllSubsteps,
  findSubstepByBackendStep,
  getMainStepForSubstep,
  type StepRenderContext,
  StepMode,
} from '../utils/stepDefinitions';
import { PostAccountStepId } from '../ActivationFlow/constants/stepIds';
import { STEP_COMPONENTS } from '../utils/stepComponents';
import { determineStepProgression } from '../utils/stepProgressionLogic';
import { useActivationProgress } from '../ActivationFlow/hooks/useActivationProgress';
import ActivationFlowErrorBoundary from './ActivationFlowErrorBoundary';
import {
  shouldAutoSkipIssueCard,
  shouldAutoSkipCardDesign,
  applyAutoSkip,
} from '../utils/autoSkipLogic';
import { bankingSignupLogger } from '../utils/bankingSignupLogger';

export interface UnifiedBankingSignupFlowProps {
  onComplete?: () => void;
  initialScreenIndex?: number;
  /** Step ID to resume from (e.g., 'card_design') - takes precedence over initialScreenIndex */
  initialStepId?: string;
}

/**
 * Unified Accountable Banking Signup Flow
 * Component onboards all users:
 * 1. pre-account (sign-up) steps;
 * 2. post-account (activation) steps.
 * - Pre-account steps work without backend yet (local state only)
 * - Post-account steps integrate with backend API (when feature flag enabled)
 * - Mode detection based on hasAccountableBanking
 */
const UnifiedBankingSignupFlow: FC<UnifiedBankingSignupFlowProps> = ({
  onComplete,
  initialScreenIndex,
  initialStepId,
}) => {
  const { t } = useTranslation();
  const hasAccountableBanking = useHasAccountableBanking();
  const { data: activationStatus, isLoading: isLoadingStatus } =
    useGetActivationStatus();
  const updateStep = useUpdateActivationStatus();

  const { data: user } = useGetCurrentCustomerData();
  const { data: cards, isLoading: isLoadingCards } = useGetCardsList();

  // Feature flag for post-account flow
  const USE_ACTIVATION_FLOW_ENABLED = useFeatures(
    'banks.usePostAccountActivationFlow',
    true,
  );

  // Determine current mode
  const currentMode: StepMode = hasAccountableBanking
    ? StepMode.PostAccount
    : StepMode.PreAccount;

  // State for step management
  const [currentStepIndex, setCurrentStepIndex] = useState(
    initialScreenIndex || 0,
  );
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Tracks steps that were reached via auto-skip from issue_card step
   * These steps will have disabled backward navigation (both back button and progress bar)
   * This is generic - any step skipped from issue_card will be tracked here
   *
   * Example use cases:
   * - Skip to your_card_ready when user already has an enabled card
   * - Skip to first_transfer when user chooses to skip card creation
   * - Any future skip scenarios from issue_card step
   */
  const [autoSkippedSteps, setAutoSkippedSteps] = useState<Set<string>>(
    new Set(),
  );

  // State for pre-account flow (WalletOnboarding state)
  const [onboardingId, setOnboardingId] = useState<string>('');
  const [accountDetails, setAccountDetails] = useState<
    IUser & {
      monthlyPaymentVolume: MonthlyPaymentVolume;
      companyName: string;
      additionalBusinessActivityDescription: string;
    }
  >();
  const [proofOfRegistrationFiles, setProofOfRegistrationFiles] = useState<
    {
      url: string;
      payload: string;
    }[]
  >([]);

  const { mutate: createBankConnection, isLoading: isCreatingConnection } =
    useCreateBankConnection(
      {
        numberOfConnectors: 0,
      },
      user?.country === AvailableCountries.germany,
    );

  const onboardingAPI = useInitiateAccountableBankingOnboarding();

  // Get all substeps and filter based on mode and feature flags
  const visibleSubsteps = useMemo(() => {
    const allSubsteps = getAllSubsteps();

    // If post-account flow is disabled, only show pre-account steps
    if (!USE_ACTIVATION_FLOW_ENABLED) {
      return allSubsteps.filter((s) => s.mode === StepMode.PreAccount);
    }

    // Filter country-specific steps
    return allSubsteps.filter((substep) => {
      if (substep?.countrySpecific) {
        return substep.countrySpecific.includes(
          user?.country as AvailableCountries,
        );
      }
      return true;
    });
  }, [USE_ACTIVATION_FLOW_ENABLED, user?.country]);

  // Initialize step based on mode
  useEffect(() => {
    if (isInitialized) return;

    // Wait for cards data to load (needed for auto-skip logic)
    if (isLoadingCards) return;

    // PRIORITY 1: Resume from specific step ID (e.g., after external redirect)
    if (initialStepId) {
      let stepIndex = visibleSubsteps.findIndex((s) => s.id === initialStepId);
      if (stepIndex !== -1) {
        bankingSignupLogger.debug(
          `Resuming from specified step: ${initialStepId} (index: ${stepIndex})`,
        );

        // Check if we should auto-skip card_design step (user has enabled card after Swan redirect)
        const cardDesignSkipResult = shouldAutoSkipCardDesign(
          cards,
          initialStepId,
        );
        if (cardDesignSkipResult.shouldSkip) {
          const targetIndex = applyAutoSkip(
            initialStepId,
            cardDesignSkipResult,
            visibleSubsteps,
          );
          if (targetIndex !== -1) {
            stepIndex = targetIndex;
            setAutoSkippedSteps(new Set([cardDesignSkipResult.targetStepId!]));
            bankingSignupLogger.autoSkip(
              initialStepId,
              cardDesignSkipResult.targetStepId!,
              cardDesignSkipResult.skipReason!,
            );
          }
        }

        setCurrentStepIndex(stepIndex);
        setIsInitialized(true);
        return;
      } else {
        bankingSignupLogger.warn(
          `Could not find step with ID: ${initialStepId}. Falling back to normal initialization.`,
        );
        // Fall through to normal initialization
      }
    }

    if (currentMode === StepMode.PostAccount && USE_ACTIVATION_FLOW_ENABLED) {
      // Post-account mode: resume from backend status
      if (isLoadingStatus) return;

      // Case 1: Backend has activation status
      if (
        activationStatus?.activationFurthestStepReached !== undefined &&
        activationStatus?.activationFurthestStepReached !== null
      ) {
        const backendStep = activationStatus.activationFurthestStepReached;

        // Find substep by backend step
        const substep = findSubstepByBackendStep(backendStep);
        if (substep) {
          let stepIndex = visibleSubsteps.findIndex((s) => s.id === substep.id);
          if (stepIndex !== -1) {
            // Check if we should auto-skip issue_card (if user has enabled card)
            const skipResult = shouldAutoSkipIssueCard(cards, substep.id);
            if (skipResult.shouldSkip) {
              const targetIndex = applyAutoSkip(
                substep.id,
                skipResult,
                visibleSubsteps,
              );
              if (targetIndex !== -1) {
                stepIndex = targetIndex;
                setAutoSkippedSteps(new Set([skipResult.targetStepId!]));
                bankingSignupLogger.autoSkip(
                  substep.id,
                  skipResult.targetStepId!,
                  skipResult.skipReason!,
                );
              }
            }
            setCurrentStepIndex(stepIndex);
          } else {
            // Couldn't find step - default to first post-account step
            bankingSignupLogger.warn(
              `Could not find substep with id: ${substep.id}. Defaulting to first post-account step.`,
            );
            const firstPostAccountIndex = visibleSubsteps.findIndex(
              (s) => s.mode === StepMode.PostAccount,
            );
            setCurrentStepIndex(Math.max(0, firstPostAccountIndex));
          }
        }
      }
      // Case 2: First time activation (null) OR backend not supported
      else {
        bankingSignupLogger.debug(
          'First time activation detected (activationFurthestStepReached is null)',
        );
        bankingSignupLogger.debug(
          'visibleSubsteps length:',
          visibleSubsteps.length,
        );
        bankingSignupLogger.debug(
          'All visible substep IDs:',
          visibleSubsteps.map((s) => `${s.id} (${s.mode})`),
        );

        // First time post-account activation - start from first post-account step WITH backendStep
        // This skips placeholder steps like 'first_transfer' that don't have backend tracking
        const firstBackendStepIndex = visibleSubsteps.findIndex(
          (s) => s.mode === StepMode.PostAccount && s.backendStep,
        );

        bankingSignupLogger.debug(
          'First post-account step with backendStep index:',
          firstBackendStepIndex,
        );

        if (firstBackendStepIndex !== -1) {
          let startIndex = firstBackendStepIndex;
          const firstStep = visibleSubsteps[firstBackendStepIndex];

          // Check if we should auto-skip issue_card (if user has enabled card)
          const skipResult = shouldAutoSkipIssueCard(cards, firstStep?.id);
          if (skipResult.shouldSkip) {
            const targetIndex = applyAutoSkip(
              firstStep.id,
              skipResult,
              visibleSubsteps,
            );
            if (targetIndex !== -1) {
              startIndex = targetIndex;
              setAutoSkippedSteps(new Set([skipResult.targetStepId!]));
              bankingSignupLogger.autoSkip(
                firstStep.id,
                skipResult.targetStepId!,
                skipResult.skipReason!,
              );
            }
          }

          bankingSignupLogger.debug(
            `First time activation - starting at step index ${startIndex}: ${visibleSubsteps[startIndex]?.id}`,
          );
          setCurrentStepIndex(startIndex);
        } else {
          // Fallback: find any post-account step
          const firstPostAccountIndex = visibleSubsteps.findIndex(
            (s) => s.mode === StepMode.PostAccount,
          );
          if (firstPostAccountIndex !== -1) {
            bankingSignupLogger.debug(
              `Fallback: starting at first post-account step ${firstPostAccountIndex}`,
            );
            setCurrentStepIndex(firstPostAccountIndex);
          } else {
            bankingSignupLogger.error('No post-account steps found!');
            setCurrentStepIndex(0);
          }
        }
      }

      setIsInitialized(true);
    } else {
      // Pre-account mode: start from beginning or specified index (no backend needed)
      setCurrentStepIndex(initialScreenIndex || 0);
      setIsInitialized(true);
    }
  }, [
    currentMode,
    USE_ACTIVATION_FLOW_ENABLED,
    activationStatus,
    isLoadingStatus,
    isInitialized,
    visibleSubsteps,
    initialScreenIndex,
    initialStepId,
    onComplete,
    cards,
    isLoadingCards,
  ]);

  // Handle step progression
  const handleContinue = () => {
    // Prevent double-clicks while update is in progress
    if (updateStep.isLoading) {
      return;
    }

    // Determine what action to take based on current step type and context
    const action = determineStepProgression({
      currentStepIndex,
      visibleSubsteps,
      isFeatureEnabled: USE_ACTIVATION_FLOW_ENABLED,
      furthestBackendStep: activationStatus?.activationFurthestStepReached,
    });

    // Execute the determined action
    switch (action.type) {
      case 'MOVE_LOCAL':
        // Simple local navigation (no backend update)
        if (action.nextIndex !== undefined) {
          setCurrentStepIndex(action.nextIndex);
        }
        break;

      case 'UPDATE_BACKEND_THEN_MOVE':
        // Update backend tracking, then navigate
        if (action.backendStepToUpdate && action.nextIndex !== undefined) {
          updateStep.mutate(action.backendStepToUpdate, {
            onSuccess: () => {
              setCurrentStepIndex(action.nextIndex!);
            },
          });
        }
        break;

      case 'UPDATE_BACKEND_THEN_COMPLETE':
        // Update backend tracking, then complete flow
        if (action.backendStepToUpdate) {
          updateStep.mutate(action.backendStepToUpdate, {
            onSuccess: () => {
              onComplete?.();
            },
          });
        }
        break;

      case 'COMPLETE':
        // Complete flow immediately (no backend update)
        onComplete?.();
        break;
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  // Handle skip to specific substep
  const handleSkip = (targetSubstepId: string) => {
    const targetIndex = visibleSubsteps.findIndex(
      (s) => s.id === targetSubstepId,
    );
    if (targetIndex !== -1) {
      const currentSubstep = visibleSubsteps[currentStepIndex];
      bankingSignupLogger.stepTransition(
        currentSubstep?.id || currentStepIndex,
        targetSubstepId,
        'user skip',
      );

      // Track auto-skipped steps (steps reached via skip from issue_card)
      // These steps should prevent backward navigation
      if (currentSubstep?.id === PostAccountStepId.IssueCard) {
        setAutoSkippedSteps((prev) => new Set(prev).add(targetSubstepId));
      }

      setCurrentStepIndex(targetIndex);
    } else {
      bankingSignupLogger.warn(`Skip target not found: ${targetSubstepId}`);
    }
  };

  // Handle navigation to a specific substep from progress bar
  // TODO: Progress bar navigation temporarily disabled - will be re-enabled with proper validation
  const handleProgressSegmentClick = (_substepId: string) => {
    return;
  };

  // Calculate progress information for the current step
  const currentSubstep = visibleSubsteps[currentStepIndex];

  // Get metadata for current step (title, subtitle, icon)
  const currentMainStep = currentSubstep
    ? getMainStepForSubstep(currentSubstep.id)
    : undefined;

  const { progressSegments, currentStepNumber, totalSteps } =
    useActivationProgress({
      visibleSubsteps,
      currentStepIndex,
      currentStepId: currentSubstep?.id || '',
      currentMainStep, // Pass current main step to filter substeps
      disableNavigation: currentSubstep?.id
        ? autoSkippedSteps.has(currentSubstep.id)
        : false,
    });

  // Prepare metadata props
  const stepTitle = currentSubstep?.titleKey
    ? t(currentSubstep.titleKey)
    : undefined;
  const stepSubtitle = currentSubstep?.subtitleKey
    ? t(currentSubstep.subtitleKey)
    : undefined;
  const stepIcon = currentSubstep?.modalIcon; // Icon is optional and substep-specific

  // Log amplitude events for substeps
  useEffect(() => {
    const substep = visibleSubsteps[currentStepIndex];
    if (!substep) return;

    if (substep.analyticsEvent) {
      logAmplitude(substep.analyticsEvent);
    }
  }, [currentStepIndex, visibleSubsteps]);

  // Build context for step rendering
  const stepContext: StepRenderContext = {
    // Navigation
    onContinue: handleContinue,
    onBack: handleBack,
    onSkip: handleSkip,

    // Progress information
    progressSegments,
    onProgressSegmentClick: handleProgressSegmentClick,
    currentStepNumber,
    totalSteps,

    // Step metadata (automatically injected)
    title: stepTitle,
    subtitle: stepSubtitle,
    icon: stepIcon,

    // Loading states - only set if current step has backend tracking
    isUpdatingStep: currentSubstep?.backendStep ? updateStep.isLoading : false,

    // State
    onboardingId,
    setOnboardingId,
    accountDetails,
    setAccountDetails,
    proofOfRegistrationFiles,
    setProofOfRegistrationFiles,

    // API
    onboardingAPI,
    createBankConnection,
    isCreatingConnection,

    // User data
    user,
    t,
  };

  // Render current substep using metadata
  const renderStep = () => {
    // Safety check: ensure we have a valid substep
    if (!currentSubstep) {
      bankingSignupLogger.error('No current substep found');
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-primary-600 mb-4">
              Unable to load this step. Please try again.
            </p>
          </div>
        </div>
      );
    }

    // Resolve component from registry
    const Component = STEP_COMPONENTS[currentSubstep.componentName];

    // Safety check: ensure component exists in registry
    if (!Component) {
      bankingSignupLogger.error(
        `Component not found in registry: ${currentSubstep.componentName}`,
      );
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-primary-600 mb-4">
              Step configuration error. Please contact support.
            </p>
            <p className="text-sm text-primary-400">
              Missing component: {currentSubstep.componentName}
            </p>
          </div>
        </div>
      );
    }

    // Generate props from context
    try {
      const props = currentSubstep.renderProps(stepContext);
      return <Component {...props} />;
    } catch (error) {
      bankingSignupLogger.error('Error generating step props:', error);
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-primary-600">Failed to render step</p>
          </div>
        </div>
      );
    }
  };

  if (
    !isInitialized ||
    (currentMode === StepMode.PostAccount && isLoadingStatus)
  ) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <CustomLoader />
      </div>
    );
  }

  return (
    <ActivationFlowErrorBoundary>
      <div className="relative w-full">
        <AnimatePresence mode="wait">
          <motion.div {...fadeTransition} key={currentSubstep?.id}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </ActivationFlowErrorBoundary>
  );
};

export default UnifiedBankingSignupFlow;
